import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpHeaders,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
    HttpResponseBase
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { HttpErrorStatusCode } from '../../shared/enums/http-request-status-code.enum';
import { ICustomResponseModel } from '../models/custom-error-response.model';
import { AuthService } from './auth.service';
import { LoaderService } from './loader.service';
import { ToasterService } from './toaster/toaster.service';
import { AppService } from './app.service';
import { TranslateService } from '@ngx-translate/core';
import { AccessControlService } from './accessControl/access-control.service';

@Injectable({
    providedIn: 'root'
})
export class InterceptorService implements HttpInterceptor {

    // list of ignored url which will not show the spinner
    private readonly _ignoredUrls: string[] = [
        '/Nmd/GetCachedPaginatedProductsByVersionDate',
        '/Nmd/GetElementsAndProductsByNlFsbCodes'
    ];

    constructor(
        private _loaderService: LoaderService,
        private _toasterService: ToasterService,
        private _authService: AuthService,
        private _appService: AppService,
        private _translate: TranslateService,
        private _accessControlService: AccessControlService
    ) {
    }

    /**
     * intercept HTTP requests and handle them before passing them
     * @param request
     * @param next
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        /* Added delay (800 milliseconds) to open spinner, to prevent blinking screen when response comes very fast
           If response comes within in 800 milliseconds then spinner will not show
        */
        let timer;
        const result = this._ignoredUrls.some(url => request.url.includes(url));

        if (!result) {
            timer = setTimeout(() => this._loaderService.loadLoader$.next(true), 800);
        }

        const accessToken = this._authService.getAccessToken();
        const isReadOnly = this._accessControlService.isReadOnly();

        // Block non-GET requests if in read-only mode
        if (isReadOnly && request.method != 'GET') {
            return EMPTY;
        }
        const authReq = request.clone({
            headers: new HttpHeaders({
                'Content-Type': ['application/json', 'application/x-www-form-urlencoded', 'charset=utf-8'],
                'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                'observe': 'response'
            })
        });

        return next.handle(authReq).pipe(
            tap(() => {
            },
                error => {
                    if (error instanceof HttpErrorResponse) {
                        if (error.status === 0) {
                            this.handleError(error);
                            return;
                        }
                        else if (error.status === HttpErrorStatusCode.UNAUTHORIZED) {
                            this._authService.login();
                            return;
                        }
                        this.transformBlobToJson(error).then((res: any) => {
                            // Skip toaster for 404 responses that carry no message
                            if (error.status === HttpErrorStatusCode.NOT_FOUND && res === "Not Found") {
                                return;
                            }
                            this.handleError(error, res);
                        });
                    }
                }
            ),
            finalize(() => {
                clearTimeout(timer);
                this._loaderService.loadLoader$.next(false);
            })
        );
    }

    /**
     * It id for handle http request error and return an observable
     * @param response
     * @param text
     */
    private handleError = (response: HttpResponseBase, text?: string): Observable<any> => {
        const errorResponse: ICustomResponseModel = {
            statusCode: response.status,
            message: text
        };
        if (errorResponse.message === this._translate.instant('MESSAGES.TRASH_BIN_EXCEPTION')) {
            this._appService.setCalculationInTrashFlag(true);
            return throwError(errorResponse);

        }
        this._toasterService.errorMethod(errorResponse);
        return throwError(response);
    };

    private transformBlobToJson = (response: HttpResponseBase): Promise<string> => {
        return new Promise(resolve => {
            const responseBlob = response instanceof HttpResponse
                ? response.body : (<any>response).error instanceof Blob
                    ? (<any>response).error : undefined;
            blobToText(responseBlob).pipe(map(responseText => {
                if (responseText !== null) {
                    const responseObject: ICustomResponseModel = JSON.parse(responseText);
                    if (responseObject.showToasterMessage != null && responseObject.showToasterMessage)
                        return responseObject.detail;
                    if (responseObject.exception != null && responseObject.exception['message'] === this._translate.instant('MESSAGES.TRASH_BIN_EXCEPTION'))
                        return responseObject.exception['message'];
                    return responseObject.message ? responseObject.message : responseObject.title;
                }
                return null;
            })).subscribe(res => {
                return resolve(res);
            });
        });
    }
}

function blobToText(blob: any): Observable<string> {
    return new Observable<string>((observer: any) => {
        if (!blob) {
            observer.next('');
            observer.complete();
        } else {
            let reader = new FileReader();
            reader.onload = event => {
                observer.next((<any>event.target).result);
                observer.complete();
            };
            reader.readAsText(blob);
        }
    });
}
